import { clickCiApiUrl } from './apiUrl';

export type CdRunStatus = 'queued' | 'running' | 'candidate_ready' | 'succeeded' | 'failed' | 'rolled_back';
export type CdStepStatus = 'pending' | 'running' | 'succeeded' | 'failed' | 'skipped';

export interface CdReleasePlan {
  id: string;
  sourceAppId?: string;
  sourceAppName?: string;
  targetAppName: string;
  targetAppId: string | null;
  targetSpaceId: string;
  environment: 'Production' | 'Staging' | 'QA';
  repository: string;
  branch: string;
  currentVersion: string | null;
  currentRevision: string | null;
  desiredVersion: string;
  desiredRevision: string;
  dataPolicy: 'reload-target-connections' | 'structure-only';
  updatedAt: string;
  configurationHistory?: CdReleasePlanConfigurationChange[];
  sourceBinding?: {
    status: 'verified';
    revision: string;
    verifiedAt: string;
    verifiedBy: string;
    evidence: 'mapped-git-head-v1';
  };
  latestDeployment?: CdDeploymentRun | null;
  targetHealth?: CdTargetHealth;
}

export interface CdTargetHealthEvidence {
  targetAppId: string;
  targetSpaceId: string;
  targetOriginAppId: string;
  publishTime: string;
}

export interface CdTargetHealth {
  status: 'verified' | 'drifted' | 'unavailable' | 'unknown';
  checkedAt: string;
  message: string;
  expected: (CdTargetHealthEvidence & { deploymentId: string; version: string; revision: string }) | null;
  observed: (CdTargetHealthEvidence & { name?: string }) | null;
  differences: Array<{ field: keyof CdTargetHealthEvidence; expected: string; observed: string }>;
}

export interface CdReleasePlanConfigurationChange {
  id: string;
  changedAt: string;
  changedBy: string;
  changes: Array<{
    field: 'targetAppName' | 'environment' | 'dataPolicy' | 'desiredVersion' | 'desiredRevision' | 'sourceAppId' | 'sourceAppName' | 'repository' | 'branch';
    from: string;
    to: string;
  }>;
}

export interface CdManagedTarget {
  appId: string;
  name: string;
  spaceId: string;
  releasePlanId: string | null;
}

export interface CdReleaseSource {
  sourceAppId: string;
  sourceAppName: string;
  repository: string;
  branch: string;
  approvedRevision: string;
  mappedRevision: string;
  lastCheckedInAt: string | null;
  status: 'available' | 'unavailable';
  message: string;
}

export interface CdArtifactRecord {
  id: string;
  planId: string;
  version: string;
  revision: string;
  fileName: string;
  bytes: number;
  sha256: string;
  createdAt: string;
  verified: boolean;
  verifiedAt?: string;
  versionedFiles?: number;
  releaseNote?: string;
  releaseNarrative?: CdArtifactReleaseNarrative;
  releaseImpact?: CdArtifactReleaseImpact;
  retention?: {
    status: 'standard' | 'protected';
    reason: string;
    updatedAt: string;
    updatedBy: string;
  };
  deletionGate?: { eligible: boolean; message: string };
  integrity?: {
    status: 'valid' | 'corrupted' | 'missing';
    checkedAt: string;
    actualSha256?: string;
    message: string;
  };
  testGate?: {
    status: 'passed' | 'failed' | 'missing' | 'stale';
    planId?: string;
    runId?: string;
    confidence?: number;
    checkedAt?: string;
    message: string;
    risk?: 'Low' | 'Medium' | 'High';
    summary?: string;
    businessImpact?: string;
    suggestedAction?: string;
    failureCount?: number;
    failedCase?: {
      id: string;
      category: string;
      title: string;
      targetId?: string;
      evidence: string;
      executionMode: string;
    };
  };
  approvalGate?: CdArtifactApprovalGate;
}

export interface CdArtifactReleaseNarrative {
  status: 'generated' | 'fallback';
  provider: 'openai' | 'deterministic';
  model: string;
  summary: string;
  businessImpact: string;
  demoTalkingPoint: string;
  generatedAt: string;
  promptVersion: 'artifact-release-narrative-v1';
  evidenceDigest: string;
  evidenceReferences: string[];
  responseId?: string;
  inputTokens?: number;
  outputTokens?: number;
  fallbackReason?: string;
}

export interface CdArtifactReleaseImpact {
  generator: 'release-impact-v1';
  generatedAt: string;
  baselineArtifactId: string;
  baselineVersion: string;
  baselineRevision: string;
  candidateVersion: string;
  candidateRevision: string;
  summary: string;
  riskLevel: 'low' | 'medium' | 'high';
  recommendation: 'safe_to_publish' | 'needs_business_review' | 'blocked';
  businessImpact: string[];
  changes: Array<{
    folder: string;
    objectId: string;
    objectType: string;
    title?: string;
    changeType: 'added' | 'removed' | 'modified';
    impactType: string;
    risk: 'low' | 'medium' | 'high';
    businessSummary: string;
  }>;
  visualReviewTargets: Array<{ objectId: string; objectType: string; title?: string; reason: string }>;
  agentReviewPrompt: string;
}

export interface CdArtifactApprovalGate {
  status: 'approved' | 'pending' | 'rejected' | 'missing' | 'stale';
  approvalId?: string;
  requestedBy?: string;
  requestedAt?: string;
  decidedBy?: string;
  decidedAt?: string;
  decisionNote?: string;
  message: string;
}

export interface CdReleaseApproval {
  id: string;
  planId: string;
  artifactId: string;
  artifactSha256: string;
  version: string;
  revision: string;
  targetAppId: string;
  environment: CdReleasePlan['environment'];
  dataPolicy: CdReleasePlan['dataPolicy'];
  testPlanId: string;
  testRunId: string;
  status: 'pending' | 'approved' | 'rejected';
  requestedBy: string;
  requestedAt: string;
  decidedBy: string | null;
  decidedAt: string | null;
  decisionNote: string | null;
}

export interface CdRunStep {
  key: string;
  title: string;
  description: string;
  status: CdStepStatus;
  detail: string;
  startedAt?: string;
  completedAt?: string;
}

export interface CdDeploymentRun {
  id: string;
  planId: string;
  mode: 'build' | 'deploy' | 'rollback';
  scenario: 'success' | 'publish_gate_failure';
  version: string;
  revision: string;
  status: CdRunStatus;
  createdAt: string;
  completedAt: string | null;
  buildAppId: string | null;
  targetAppId: string | null;
  artifactId: string | null;
  error: string | null;
  liveImpact: string;
  steps: CdRunStep[];
  verification?: {
    artifactSha256: string;
    targetAppId: string;
    targetSpaceId: string;
    targetOriginAppId: string;
    publishTime: string;
  };
  testEvidence?: {
    planId: string;
    runId: string;
    confidence: number;
    artifactSha256: string;
    checkedAt: string;
  };
  approvalEvidence?: {
    approvalId: string;
    requestedBy: string;
    decidedBy: string;
    decidedAt: string;
    artifactSha256: string;
    targetAppId: string;
    environment: CdReleasePlan['environment'];
    dataPolicy: CdReleasePlan['dataPolicy'];
  };
}

export interface CdDeploymentIncident {
  id: string;
  planId: string;
  deploymentId: string;
  artifactId: string | null;
  artifactSha256: string | null;
  targetAppId: string | null;
  category: 'quality_gate' | 'policy_gate' | 'artifact_integrity' | 'deployment_task';
  severity: 'Medium' | 'High';
  status: 'open' | 'resolved';
  title: string;
  rootCause: string;
  liveImpact: string;
  suggestedAction: string;
  createdAt: string;
  resolvedAt: string | null;
  resolutionDeploymentId: string | null;
}

interface ResultEnvelope<T> { result?: T; message?: string }
const apiUrl = clickCiApiUrl;

async function request<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${apiUrl}${url}`, init);
  const payload = await response.json() as ResultEnvelope<T>;
  if (!response.ok || payload.result === undefined) throw new Error(payload.message ?? `Request failed (${response.status})`);
  return payload.result;
}

export const cdDemoApi = {
  listPlans: () => request<{ plans: CdReleasePlan[] }>('/api/demo-cd/plans'),
  listTargets: () => request<{ targets: CdManagedTarget[] }>('/api/demo-cd/targets'),
  listSources: () => request<{ sources: CdReleaseSource[] }>('/api/demo-cd/sources'),
  createPlan: (body: { targetAppId: string; targetAppName: string; sourceAppId: string; sourceRevision: string; environment: CdReleasePlan['environment']; dataPolicy: CdReleasePlan['dataPolicy'] }) =>
    request<CdReleasePlan>('/api/demo-cd/plans', {
      method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body),
    }),
  updatePlan: (id: string, body: Partial<Pick<CdReleasePlan, 'targetAppName' | 'environment' | 'dataPolicy' | 'desiredVersion' | 'desiredRevision'>>) =>
    request<CdReleasePlan>(`/api/demo-cd/plans/${id}`, {
      method: 'PATCH', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body),
    }),
  getPlan: (id: string) => request<{ plan: CdReleasePlan; deployments: CdDeploymentRun[]; artifacts: CdArtifactRecord[]; incidents: CdDeploymentIncident[]; approvals: CdReleaseApproval[] }>(`/api/demo-cd/plans/${id}`),
  requestApproval: (planId: string, artifactId: string) => request<CdReleaseApproval>(`/api/demo-cd/plans/${planId}/approvals`, {
    method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ artifactId }),
  }),
  decideApproval: (approvalId: string, decision: 'approved' | 'rejected', note: string) => request<CdReleaseApproval>(`/api/demo-cd/approvals/${approvalId}/decision`, {
    method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ decision, note }),
  }),
  generateArtifactImpact: (planId: string, artifactId: string, baselineArtifactId?: string) => request<CdArtifactRecord>(`/api/demo-cd/plans/${planId}/artifacts/${artifactId}/impact`, {
    method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ ...(baselineArtifactId ? { baselineArtifactId } : {}) }),
  }),
  updateArtifactRetention: (planId: string, artifactId: string, status: 'standard' | 'protected', reason?: string) => request<CdArtifactRecord>(`/api/demo-cd/plans/${planId}/artifacts/${artifactId}/retention`, {
    method: 'PATCH', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ status, ...(reason ? { reason } : {}) }),
  }),
  deleteArtifact: (planId: string, artifactId: string) => request<{ deletedArtifactId: string; fileName: string }>(`/api/demo-cd/plans/${planId}/artifacts/${artifactId}`, { method: 'DELETE' }),
  getDeployment: (id: string) => request<CdDeploymentRun>(`/api/demo-cd/deployments/${id}`),
  deploymentEvidenceDownloadUrl: (id: string) => `${apiUrl}/api/demo-cd/deployments/${encodeURIComponent(id)}/evidence`,
  startDeployment: (planId: string, body: { version?: string; revision?: string; scenario?: 'success' | 'publish_gate_failure'; artifactId?: string; mode?: 'build' | 'deploy' | 'rollback'; rollbackArtifactId?: string }) =>
    request<CdDeploymentRun>(`/api/demo-cd/plans/${planId}/deployments`, {
      method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body),
    }),
  artifactDownloadUrl: (planId: string, artifactId: string) => `${apiUrl}/api/demo-cd/plans/${planId}/artifacts/${artifactId}/download`,
};
